import React, { useState, useCallback } from 'react';
import {
  Table,
  Button,
  Space,
  Popconfirm,
  Typography,
  Card,
  Modal,
  Form,
  Input,
  Select,
  Avatar,
  Tag,
  Tooltip,
  Alert,
  message,
  Divider,
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  EditOutlined,
  UserOutlined,
  PictureOutlined,
  RobotOutlined,
  ScanOutlined,
  MinusCircleOutlined,
  MergeCellsOutlined,
} from '@ant-design/icons';
import CharacterImageModal from './CharacterImageModal';
import { SourceCharacter } from '../../types';
import { sourceStoriesApi, CreateSourceCharacterRequest } from '../../services/api';
import { useApi, useAsyncOperation } from '../../hooks';
import { formatDate } from '../../utils';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

interface CharactersTabProps {
  storyId: string;
}

const CharactersTab: React.FC<CharactersTabProps> = ({ storyId }) => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isImageModalVisible, setIsImageModalVisible] = useState(false);
  const [isSuggestingName, setIsSuggestingName] = useState(false);
  const [scanningCharacterId, setScanningCharacterId] = useState<string | null>(null);
  const [editingCharacter, setEditingCharacter] = useState<SourceCharacter | null>(null);
  const [selectedCharacter, setSelectedCharacter] = useState<SourceCharacter | null>(null);
  const [isMergeModalVisible, setIsMergeModalVisible] = useState(false);
  const [mergeTargetCharacter, setMergeTargetCharacter] = useState<SourceCharacter | null>(null);
  const [form] = Form.useForm();

  const { loading: asyncLoading, execute: executeAsync } = useAsyncOperation();

  // Fetch characters
  const {
    data: charactersData,
    loading: charactersLoading,
    refresh: refreshCharacters,
  } = useApi(
    useCallback(() => sourceStoriesApi.getSourceCharacters(storyId), [storyId]),
    { immediate: true }
  );

  const handleCreate = () => {
    setEditingCharacter(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  const handleEdit = (character: SourceCharacter) => {
    setEditingCharacter(character);
    form.setFieldsValue({
      ...character,
      localizedName: character.localizedData?.name,
      appearanceOverride: character.appearanceOverride 
        ? Object.entries(character.appearanceOverride).map(([key, value]) => ({ key, value }))
        : [],
    });
    setIsModalVisible(true);
  };

  const handleDeepScan = async (character: SourceCharacter) => {
    setScanningCharacterId(character.id);
    try {
      const result = await sourceStoriesApi.deepScanCharacter(storyId, character.id);
      const { enhancedDescription, aliases, confidence, reasoning } = result.data;

      Modal.confirm({
        title: 'Deep Scan Result',
        width: 700,
        content: (
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <Alert
              type="warning"
              showIcon
              message="Deep Scan Limitation"
              description="Deep scan only enriches INITIAL appearance (first chapter). If the character changes appearance over time (hair dye, scars, aging), those changes will be IGNORED. Use the Checkpoint system for time-based changes."
              style={{ marginBottom: 8 }}
            />
            <div>
              <Text strong>Confidence:</Text> <Tag color={confidence === 'HIGH' ? 'green' : confidence === 'MEDIUM' ? 'orange' : 'red'}>{confidence}</Tag>
            </div>
            <div>
              <Text strong>Reasoning:</Text>
              <div style={{ background: '#f5f5f5', padding: 8, borderRadius: 4, marginTop: 4 }}>{reasoning}</div>
            </div>
            <div>
              <Text strong>Enhanced Description:</Text>
              <div style={{ background: '#f5f5f5', padding: 8, borderRadius: 4, marginTop: 4, whiteSpace: 'pre-wrap' }}>{enhancedDescription}</div>
            </div>
            {aliases.length > 0 && (
              <div>
                <Text strong>Aliases found:</Text>{' '}
                {aliases.map((a: string) => <Tag key={a}>{a}</Tag>)}
              </div>
            )}
          </Space>
        ),
        okText: 'Apply',
        cancelText: 'Dismiss',
        onOk: async () => {
          await executeAsync(
            () => sourceStoriesApi.updateSourceCharacter(storyId, character.id, {
              description: enhancedDescription,
              aliases,
            }),
            { successMessage: 'Deep scan results applied!' }
          );
          refreshCharacters();
        },
      });
    } catch (error: any) {
      message.error(error.message || 'Deep scan failed');
    } finally {
      setScanningCharacterId(null);
    }
  };

  const handleDelete = async (character: SourceCharacter) => {
    await executeAsync(
      () => sourceStoriesApi.deleteSourceCharacter(storyId, character.id),
      { successMessage: 'Character deleted successfully!' }
    );
    refreshCharacters();
  };

  const handleManageImages = (character: SourceCharacter) => {
    setSelectedCharacter(character);
    setIsImageModalVisible(true);
  };

  const handleMerge = (character: SourceCharacter) => {
    setMergeTargetCharacter(character);
    setIsMergeModalVisible(true);
  };

  const handleConfirmMerge = async (duplicateCharacterId: string) => {
    if (!mergeTargetCharacter) return;

    await executeAsync(
      () => sourceStoriesApi.mergeCharacters(storyId, mergeTargetCharacter.id, duplicateCharacterId),
      { 
        successMessage: `Successfully merged into "${mergeTargetCharacter.name}"! ${mergeTargetCharacter.name} now has the old character's name as an alias.` 
      }
    );

    setIsMergeModalVisible(false);
    setMergeTargetCharacter(null);
    refreshCharacters();
  };

  const handleSuggestName = async () => {
    const values = form.getFieldsValue();
    if (!values.name) {
      message.warning('Please enter character original name first');
      return;
    }
    
    setIsSuggestingName(true);
    try {
      const characterId = editingCharacter?.id || 'new';
      const result = await sourceStoriesApi.suggestCharacterName(storyId, characterId, {
        name: values.name,
        description: values.description
      });
      if (result.data.suggestedName) {
        form.setFieldsValue({ localizedName: result.data.suggestedName });
        message.success(`AI suggested: ${result.data.suggestedName}`);
      }
    } catch (error) {
      console.error('Failed to suggest name:', error);
      message.error('Failed to suggest name with AI');
    } finally {
      setIsSuggestingName(false);
    }
  };

  const handleSubmit = async (values: any) => {
    const { localizedName, appearanceOverride, ...rest } = values;

    // Convert array of {key, value} to JSON object
    let parsedOverride = null;
    if (appearanceOverride && Array.isArray(appearanceOverride) && appearanceOverride.length > 0) {
      parsedOverride = {};
      appearanceOverride.forEach((item: any) => {
        if (item && item.key && item.value) {
          parsedOverride[item.key] = item.value;
        }
      });
      // If no valid key-value pairs, set to null
      if (Object.keys(parsedOverride).length === 0) {
        parsedOverride = null;
      }
    }

    const finalValues = {
      ...rest,
      appearanceOverride: parsedOverride,
      localizedData: {
        ...(editingCharacter?.localizedData || {}),
        name: localizedName || ''
      }
    };

    if (editingCharacter) {
      await executeAsync(
        () => sourceStoriesApi.updateSourceCharacter(storyId, editingCharacter.id, finalValues),
        { successMessage: 'Character updated successfully!' }
      );
    } else {
      await executeAsync(
        () => sourceStoriesApi.createSourceCharacter(storyId, finalValues as CreateSourceCharacterRequest),
        { successMessage: 'Character created successfully!' }
      );
    }
    setIsModalVisible(false);
    refreshCharacters();
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'PROTAGONIST':
        return 'gold';
      case 'ANTAGONIST':
        return 'volcano';
      case 'SUPPORTING':
        return 'blue';
      case 'BACKGROUND':
        return 'gray';
      default:
        return undefined;
    }
  };

  const columns = [
    {
      title: 'Character',
      key: 'character',
      render: (_: any, record: SourceCharacter) => (
        <Space>
          <Avatar 
            src={record.avatarUrl ? <img src={record.avatarUrl} alt={record.name} style={{ objectPosition: 'top center' }} /> : undefined}
            icon={<UserOutlined />} 
            style={{ backgroundColor: getRoleColor(record.role || '') }}
          />
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <Space>
              <Typography.Text strong>{record.name}</Typography.Text>
              {record.localizedData?.name && (
                <Typography.Text type="success" style={{ fontSize: '12px' }}>
                  ({record.localizedData.name})
                </Typography.Text>
              )}
            </Space>
            <Typography.Text type="secondary" style={{ fontSize: '12px' }}>
              {record.role}
            </Typography.Text>
          </div>
        </Space>
      ),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      render: (_: string, record: SourceCharacter) => (
        <Space direction="vertical" size={2}>
          <Text ellipsis style={{ maxWidth: 300 }}>{record.description}</Text>
          {record.aliases && record.aliases.length > 0 && (
            <Tooltip title={`Internal aliases for duplicate detection: ${record.aliases.join(', ')}`}>
              <Tag 
                icon={<span style={{ fontSize: 10 }}>🔗</span>} 
                color="default" 
                style={{ fontSize: 10, lineHeight: '16px', padding: '0 6px', cursor: 'help' }}
              >
                {record.aliases.length} alias{record.aliases.length > 1 ? 'es' : ''}
              </Tag>
            </Tooltip>
          )}
          {record.appearanceOverride && (
            <Tag color="purple" style={{ fontSize: 10 }}>Has Override</Tag>
          )}
        </Space>
      ),
    },
    {
      title: 'Created Date',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => formatDate(date),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 250,
      render: (_: any, record: SourceCharacter) => (
        <Space>
          <Tooltip title="Deep Scan (AI phân tích sâu nhân vật)">
            <Button
              type="text"
              icon={<ScanOutlined />}
              onClick={() => handleDeepScan(record)}
              loading={scanningCharacterId === record.id}
            />
          </Tooltip>
          <Tooltip title="Merge (Gộp nhân vật trùng)">
            <Button
              type="text"
              icon={<MergeCellsOutlined />}
              onClick={() => handleMerge(record)}
            />
          </Tooltip>
          <Button
            type="text"
            icon={<PictureOutlined />}
            onClick={() => handleManageImages(record)}
            title="Visual Identity"
          />
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            title="Edit"
          />
          <Popconfirm
            title="Are you sure you want to delete this character?"
            onConfirm={() => handleDelete(record)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              title="Delete"
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Card>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={3} style={{ margin: 0 }}>
          Character Management
        </Title>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleCreate}
        >
          Add Character
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={charactersData?.data || []}
        rowKey="id"
        loading={charactersLoading}
      />

      <Modal
        title={editingCharacter ? 'Edit Character' : 'Add New Character'}
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
        width={650}
        styles={{ body: { maxHeight: 'calc(100vh - 250px)', overflowY: 'auto' } }}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="name"
            label="Character Name"
            rules={[{ required: true, message: 'Please enter character name' }]}
          >
            <Input placeholder="Enter name" />
          </Form.Item>

          <Form.Item
            name="localizedName"
            label="Localized Name"
          >
            <Input 
              placeholder="Enter name for target market" 
              suffix={
                <Button
                  type="text"
                  size="small"
                  icon={<RobotOutlined />}
                  loading={isSuggestingName}
                  onClick={handleSuggestName}
                  title="AI Suggest Name"
                />
              }
            />
          </Form.Item>

          <Form.Item
            name="role"
            label="Role"
            rules={[{ required: true, message: 'Please select role' }]}
          >
            <Select placeholder="Select role">
              <Option value="PROTAGONIST">Protagonist</Option>
              <Option value="ANTAGONIST">Antagonist</Option>
              <Option value="SUPPORTING">Supporting</Option>
              <Option value="BACKGROUND">Background</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="avatarUrl"
            label="Avatar URL"
          >
            <Input placeholder="Enter avatar image URL" />
          </Form.Item>

          <Form.Item
            name="description"
            label="Description (AI original)"
            extra={<Text type="secondary" style={{ fontSize: 11 }}>Base description from AI (read-only). Use Appearance Override below to change appearance.</Text>}
          >
            <TextArea 
              rows={3} 
              placeholder="Describe character personality, background, etc." 
              disabled
              style={{ backgroundColor: '#f5f5f5', color: 'rgba(0,0,0,0.65)' }}
            />
          </Form.Item>

          <Divider>
            <Space>
              <span>Appearance Override</span>
              <Tag color="purple" style={{ fontSize: 10 }}>Priority</Tag>
            </Space>
          </Divider>


          <Form.List name="appearanceOverride">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }) => (
                  <Space key={key} style={{ display: 'flex', marginBottom: 8 }} align="baseline">
                    <Form.Item
                      {...restField}
                      name={[name, 'key']}
                      rules={[{ required: true, message: 'Key required' }]}
                      style={{ marginBottom: 0, width: 120 }}
                    >
                      <Input placeholder="e.g., face" />
                    </Form.Item>
                    <Form.Item
                      {...restField}
                      name={[name, 'value']}
                      rules={[{ required: true, message: 'Value required' }]}
                      style={{ marginBottom: 0, flex: 1 }}
                    >
                      <Input placeholder="e.g., round with big eyes" />
                    </Form.Item>
                    <MinusCircleOutlined onClick={() => remove(name)} style={{ color: '#ff4d4f' }} />
                  </Space>
                ))}
                <Form.Item style={{ marginBottom: 8 }}>
                  <Button
                    type="dashed"
                    onClick={() => add()}
                    block
                    icon={<PlusOutlined />}
                  >
                    Add Appearance Field
                  </Button>
                </Form.Item>
                <div style={{ fontSize: 11, color: '#8c8c8c', marginBottom: 16 }}>
                  Common keys: <Text code style={{ fontSize: 10 }}>face</Text>, <Text code style={{ fontSize: 10 }}>body</Text>, <Text code style={{ fontSize: 10 }}>hair</Text>, <Text code style={{ fontSize: 10 }}>clothing</Text>, <Text code style={{ fontSize: 10 }}>accessories</Text>
                </div>
              </>
            )}
          </Form.List>
          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setIsModalVisible(false)}>
                Cancel
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={asyncLoading}
              >
                {editingCharacter ? 'Update' : 'Create'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {selectedCharacter && (
        <CharacterImageModal
          visible={isImageModalVisible}
          character={selectedCharacter}
          storyId={storyId}
          onClose={() => {
            setIsImageModalVisible(false);
            setSelectedCharacter(null);
            refreshCharacters();
          }}
        />
      )}

      <Modal
        title={`Merge Character into "${mergeTargetCharacter?.name}"`}
        open={isMergeModalVisible}
        onCancel={() => {
          setIsMergeModalVisible(false);
          setMergeTargetCharacter(null);
        }}
        footer={null}
        width={700}
      >
        {mergeTargetCharacter && (
          <div>
            <Alert
              type="warning"
              showIcon
              message="Merge Characters"
              description={
                <div>
                  <p>Select a duplicate character to merge into <strong>{mergeTargetCharacter.name}</strong>.</p>
                  <p>This action will:</p>
                  <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
                    <li>Transfer all panels, scenes, and processes from the duplicate to {mergeTargetCharacter.name}</li>
                    <li>Add the duplicate's name as an alias to prevent future duplicates</li>
                    <li>Delete the duplicate character permanently</li>
                  </ul>
                </div>
              }
              style={{ marginBottom: 16 }}
            />

            <div style={{ marginBottom: 16 }}>
              <Text strong>Primary Character (Keep):</Text>
              <div style={{ background: '#f0f0f0', padding: 12, borderRadius: 4, marginTop: 8 }}>
                <Space>
                  <Avatar src={mergeTargetCharacter.avatarUrl} icon={<UserOutlined />} />
                  <div>
                    <div><strong>{mergeTargetCharacter.name}</strong></div>
                    <div style={{ fontSize: 12, color: '#666' }}>{mergeTargetCharacter.role}</div>
                  </div>
                </Space>
              </div>
            </div>

            <div style={{ marginBottom: 16 }}>
              <Text strong>Select Duplicate Character (Will be deleted):</Text>
              <Select
                style={{ width: '100%', marginTop: 8 }}
                placeholder="Select character to merge..."
                showSearch
                filterOption={(input, option: any) =>
                  option?.children?.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
                onSelect={(value: string) => handleConfirmMerge(value)}
              >
                {charactersData?.data
                  ?.filter((c: SourceCharacter) => c.id !== mergeTargetCharacter.id)
                  .map((c: SourceCharacter) => (
                    <Option key={c.id} value={c.id}>
                      <Space>
                        <Avatar size="small" src={c.avatarUrl} icon={<UserOutlined />} />
                        <span>{c.name}</span>
                        <Tag color={getRoleColor(c.role || '')}>{c.role}</Tag>
                      </Space>
                    </Option>
                  ))}
              </Select>
            </div>

            <Alert
              type="info"
              showIcon
              message="Use Case"
              description={
                <div>
                  <strong>Example:</strong> Character appears masked as "Mysterious Man" in early chapters, 
                  then reveals identity as "Nguyễn Văn A" later. You can merge "Mysterious Man" into "Nguyễn Văn A" 
                  to consolidate all references.
                </div>
              }
            />
          </div>
        )}
      </Modal>
    </Card>
  );
};

export default CharactersTab;
